import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiError, asArray } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Link as LinkIcon, Trash2, Image as ImageIcon, Video, Copy, X, Search } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const TYPE_BADGE = {
    image: { Icon: ImageIcon, label: "Image", cls: "bg-blue-50 text-[#0060A7]" },
    video: { Icon: Video, label: "Video", cls: "bg-purple-50 text-purple-700" },
};

export default function AdminMediaLibrary() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [adding, setAdding] = useState(null);  // { type, source } | null
    const [busy, setBusy] = useState(false);
    const [viewer, setViewer] = useState(null);

    const load = () => api.get("/admin/media-library").then((r) => setItems(asArray(r.data, "items"))).catch(() => {});
    useEffect(() => { load(); }, []);

    const filtered = items.filter((a) => {
        if (filter !== "all" && a.type !== filter) return false;
        if (!q) return true;
        const hay = (a.title + " " + (a.tags || []).join(" ") + " " + a.url).toLowerCase();
        return hay.includes(q.toLowerCase());
    });

    const upload = async (file, type, title) => {
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.post("/admin/media-library", { type, source: "upload", file_id: data.id, title: title || file.name, tags: [] });
            toast.success("تم الرفع");
            setAdding(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    const addByUrl = async ({ url, type, title }) => {
        if (!url) return toast.error("الرابط مطلوب");
        setBusy(true);
        try {
            await api.post("/admin/media-library", { type, source: "url", url, title: title || url, tags: [] });
            toast.success("تمت الإضافة");
            setAdding(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    const del = async (id) => {
        if (!window.confirm("حذف هذا العنصر من المكتبة؟")) return;
        await api.delete(`/admin/media-library/${id}`);
        load(); toast.success("تم الحذف");
    };

    const copyUrl = (a) => {
        const u = a.source === "upload" ? fileUrl(a.file_id) : a.url;
        navigator.clipboard.writeText(u);
        toast.success("تم نسخ الرابط");
    };

    return (
        <div data-testid="admin-media-library-page" className="max-w-7xl">
            <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
                <div>
                    <h1 className="heading text-2xl tracking-tight font-bold">مكتبة الوسائط</h1>
                    <p className="text-sm text-[#6B7280] mt-1">
                        ارفع الصور والفيديوهات مباشرة أو أضفها عبر روابط خارجية. متاحة لجميع المحررين.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setAdding({ type: "image", source: "upload" })}
                        className="bg-[#002951] hover:bg-[#0060A7] text-white px-4 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2"
                        data-testid="media-upload-image-btn">
                        <Upload size={14} /> صورة
                    </button>
                    <button onClick={() => setAdding({ type: "video", source: "upload" })}
                        className="bg-[#002951] hover:bg-[#0060A7] text-white px-4 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2"
                        data-testid="media-upload-video-btn">
                        <Upload size={14} /> فيديو
                    </button>
                    <button onClick={() => setAdding({ type: "image", source: "url" })}
                        className="border border-black/15 text-[#002951] px-4 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2"
                        data-testid="media-add-url-btn">
                        <LinkIcon size={14} /> من رابط
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 mb-6 flex-wrap bg-white p-3">
                <div className="flex-1 min-w-[220px] flex items-center gap-2 px-3 border-b border-black/10">
                    <Search size={14} className="text-[#6B7280]" />
                    <input className="bg-transparent outline-none py-2 w-full text-sm"
                        placeholder="ابحث بالاسم أو الوسم…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="media-search" />
                </div>
                {[
                    { k: "all", label: `الكل (${items.length})` },
                    { k: "image", label: `صور (${items.filter(i => i.type === "image").length})` },
                    { k: "video", label: `فيديو (${items.filter(i => i.type === "video").length})` },
                ].map((f) => (
                    <button key={f.k} onClick={() => setFilter(f.k)}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.2em] ${filter === f.k ? "bg-[#002951] text-white" : "text-[#6B7280] hover:text-[#002951]"}`}
                        data-testid={`media-filter-${f.k}`}>
                        {f.label}
                    </button>
                ))}
            </div>

            {/* Grid */}
            {filtered.length === 0 ? (
                <div className="bg-white p-16 text-center text-[#6B7280]">
                    {items.length === 0 ? "ابدأ بإضافة أول عنصر إلى مكتبتك." : "لا توجد نتائج."}
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {filtered.map((a) => {
                        const isImg = a.type === "image";
                        const src = a.source === "upload" ? fileUrl(a.file_id) : a.url;
                        const Badge = TYPE_BADGE[a.type] || TYPE_BADGE.image;
                        return (
                            <div key={a.id} className="bg-white group relative" data-testid={`media-item-${a.id}`}>
                                <button onClick={() => setViewer({ ...a, src })} className="block w-full aspect-square overflow-hidden bg-black/5">
                                    {isImg ? (
                                        <img src={src} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-[#002951] text-white">
                                            <Video size={32} />
                                        </div>
                                    )}
                                </button>
                                <div className="p-3 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[10px] uppercase px-1.5 py-0.5 ${Badge.cls}`}>{Badge.label}</span>
                                        <span className="text-[10px] text-[#6B7280] uppercase">{a.source}</span>
                                    </div>
                                    <div className="text-xs font-medium truncate" title={a.title}>{a.title}</div>
                                    <div className="flex items-center justify-between">
                                        <button onClick={() => copyUrl(a)} className="text-[10px] uppercase tracking-[0.2em] text-[#0060A7] hover:underline inline-flex items-center gap-1" data-testid={`media-copy-${a.id}`}>
                                            <Copy size={11} /> نسخ
                                        </button>
                                        <button onClick={() => del(a.id)} className="text-red-500 p-1 hover:bg-red-50" data-testid={`media-delete-${a.id}`}>
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add modal */}
            {adding && (
                <AddModal
                    adding={adding} setAdding={setAdding} busy={busy}
                    onUpload={upload} onUrl={addByUrl}
                />
            )}

            {/* Viewer modal */}
            {viewer && (
                <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6" onClick={() => setViewer(null)}>
                    <button onClick={() => setViewer(null)} className="absolute top-4 right-4 text-white p-2 hover:bg-white/10">
                        <X size={20} />
                    </button>
                    {viewer.type === "image" ? (
                        <img src={viewer.src} alt="" className="max-w-full max-h-[88vh] object-contain" />
                    ) : (
                        <video src={viewer.src} controls className="max-w-full max-h-[88vh]" />
                    )}
                </div>
            )}
        </div>
    );
}

function AddModal({ adding, setAdding, busy, onUpload, onUrl }) {
    const inputRef = useRef();
    const [url, setUrl] = useState("");
    const [title, setTitle] = useState("");
    const [type, setType] = useState(adding.type);
    const isUrl = adding.source === "url";

    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setAdding(null)}>
            <div className="bg-white max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="media-add-modal">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="heading text-lg font-bold">
                        {isUrl ? "إضافة عبر رابط" : `رفع ${type === "image" ? "صورة" : "فيديو"}`}
                    </h2>
                    <button onClick={() => setAdding(null)}><X size={18} /></button>
                </div>

                {isUrl && (
                    <div className="mb-5">
                        <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">النوع</label>
                        <div className="flex gap-3 mt-2">
                            {["image", "video"].map((t) => (
                                <button key={t} onClick={() => setType(t)}
                                    className={`flex-1 py-2 text-sm border ${type === t ? "border-[#0060A7] text-[#0060A7]" : "border-black/15 text-[#6B7280]"}`}>
                                    {t === "image" ? "صورة" : "فيديو"}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mb-5">
                    <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">العنوان (اختياري)</label>
                    <input value={title} onChange={(e) => setTitle(e.target.value)}
                        className="w-full bg-transparent border-b border-black/15 focus:border-[#0060A7] outline-none py-2"
                        placeholder="مثلاً: شعار ليجيندز الجديد" data-testid="media-input-title" />
                </div>

                {isUrl ? (
                    <div className="mb-6">
                        <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">الرابط</label>
                        <input value={url} onChange={(e) => setUrl(e.target.value)}
                            className="w-full bg-transparent border-b border-black/15 focus:border-[#0060A7] outline-none py-2"
                            placeholder="https://..." data-testid="media-input-url" />
                    </div>
                ) : (
                    <div className="mb-6">
                        <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">الملف</label>
                        <input ref={inputRef} type="file"
                            accept={type === "image" ? "image/*" : "video/*"}
                            className="block w-full text-sm mt-2"
                            data-testid="media-input-file" />
                    </div>
                )}

                <div className="flex justify-end gap-3">
                    <button onClick={() => setAdding(null)} className="px-5 py-2.5 text-sm uppercase tracking-[0.2em] border border-black/15">إلغاء</button>
                    <button disabled={busy}
                        onClick={() => {
                            if (isUrl) onUrl({ url, type, title });
                            else {
                                const f = inputRef.current?.files?.[0];
                                if (!f) return;
                                onUpload(f, type, title);
                            }
                        }}
                        className="bg-[#002951] hover:bg-[#0060A7] text-white px-6 py-2.5 text-sm uppercase tracking-[0.2em] disabled:opacity-50"
                        data-testid="media-add-submit">
                        {busy ? "جاري…" : (isUrl ? "إضافة" : "رفع")}
                    </button>
                </div>
            </div>
        </div>
    );
}
